// src/pages/auth/Login.jsx
import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { appNavigation, urlMap } from '../../utils';
import Footer from '../components/Footer';

function getQueryParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

class Login extends Component {
  state = {
    email: '',
    password: '',
    error: null,
    redirect: false,
    notice: null, // 👈 add
  };

  componentDidMount() {
    const verified = getQueryParam('verified');   // "1" | "already" | null
    const expired  = getQueryParam('expired');    // "1" | null
    const email    = getQueryParam('email');      // present if expired

    if (verified === '1') {
      this.setState({ notice: { type: 'success', text: 'Email verified — you can sign in now.' } });
    } else if (verified === 'already') {
      this.setState({ notice: { type: 'info', text: 'This email was already verified. You can sign in.' } });
    } else if (expired === '1') {
      this.setState({
        notice: {
          type: 'warning',
          text: `Your verification link expired. ${email ? 'We can resend to ' + email + '.' : ''}`
        }
      });
    }
  }

  handleChange = (e) => this.setState({ [e.target.name]: e.target.value });

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ error: null }); // reset old error

    try {
      const res = await fetch(urlMap.login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: this.state.email, password: this.state.password }),
      });

      // Try to read JSON message from server (fallbacks handled below)
      const payload = await res.json().catch(() => null);
      const apiMsg =
        (payload && (payload.message || payload.error || payload.detail)) || null;

      if (!res.ok) {
        // 🔹 explicit handling for common codes so the UI always shows something useful

        // 400 — bad request (missing email/password, malformed)
        if (res.status === 400) {
          this.setState({
            error: apiMsg || 'Invalid request. Please check your email and password.',
            notice: null,
          });
          return;
        }

        // 401 — invalid credentials
        if (res.status === 401) {
          this.setState({
            error: apiMsg || 'Invalid email or password.',
            notice: null,
          });
          return;
        }

        // 403 — not verified or locked
        if (res.status === 403) {
          // Show as a notice (yellow). If backend returns lock message, it’ll display here too.
          this.setState({
            notice: {
              type: 'warning',
              text:
                apiMsg ||
                'Your account is not verified yet. Please check your email for the verification link.',
            },
            error: null,
          });
          return;
        }

        // 404 — endpoint missing (defensive; some proxies return this)
        if (res.status === 404) {
          this.setState({
            error: apiMsg || 'Service temporarily unavailable. Please try again.',
            notice: null,
          });
          return;
        }

        // 429 — too many requests / rate limited
        if (res.status === 429) {
          this.setState({
            notice: {
              type: 'warning',
              text: apiMsg || 'Too many attempts. Please wait a moment and try again.',
            },
            error: null,
          });
          return;
        }

        // 5xx — server errors
        if (res.status >= 500) {
          this.setState({
            error: apiMsg || 'Server error. Please try again shortly.',
            notice: null,
          });
          return;
        }

        // Fallback: unknown non-OK
        throw new Error(apiMsg || `Login failed (code ${res.status}).`);
      }

      // Success
      const data = payload || {};
      if (!data.token) throw new Error('Login succeeded but no token returned.');
      localStorage.setItem('token', data.token);
      this.setState({ redirect: true });
    } catch (err) {
      this.setState({ error: err.message || 'Something went wrong. Please try again.' });
    }
  }

  resendVerification = async () => {
    try {
      const res = await fetch(urlMap.resendVerification, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: this.state.email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Could not resend verification email.');
      }

      this.setState({
        notice: {
          type: 'success',
          text: `A new verification link was sent to ${this.state.email}. Please check your inbox.`,
        },
        error: null,
      });
    } catch (err) {
      this.setState({
        notice: {
          type: 'warning',
          text: err.message || 'Resend failed. Try again later.',
        },
      });
    }
  };

  render() {
    if (this.state.redirect) return <Navigate to={appNavigation.home} />;

    const { notice } = this.state;

    return (
      <div className="login-page">
        <div className="login-wrapper">
          <form className="login-form" onSubmit={this.handleSubmit}>
            {notice && (
              <div
                className={`notice ${notice.type}`}
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background:
                    notice.type === 'success' ? '#e8f8ee' :
                    notice.type === 'warning' ? '#fff7e6' :
                    '#eef2ff',
                  color:
                    notice.type === 'success' ? '#146c2e' :
                    notice.type === 'warning' ? '#8a6d1d' :
                    '#3730a3',
                  border: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                {notice.text}

                {/* Resend button only if account not verified */}
                {notice.type === 'warning' && notice.text && notice.text.toLowerCase().includes('not verified') && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={this.resendVerification}
                      style={{
                        background: '#facc15',
                        color: '#000',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                      }}
                    >
                      Resend Verification Email
                    </button>
                  </div>
                )}
              </div>
            )}

            <h2>Welcome Back</h2>
            <p className="subtitle">Please login to your Storyclub account</p>

            <input
              type="email" name="email" placeholder="Email"
              value={this.state.email} onChange={this.handleChange} required
            />
            <input
              type="password" name="password" placeholder="Password"
              value={this.state.password} onChange={this.handleChange} required
            />

            <button type="submit">Login</button>

            {this.state.error && <p className="error-text">{this.state.error}</p>}

            <p className="footer-text"><Link to={appNavigation.recoverPassword}>Forgot your password?</Link></p>
            <p className="footer-text">Don’t have an account? <Link to={appNavigation.register}>Register here</Link></p>
          </form>
        </div>
        <Footer />
      </div>
    );
  }
}

export default Login;
